import * as joint from '@joint/core'

/**
 * C4 Relationship - link with arrow and label
 */
export const C4Relationship = joint.shapes.standard.Link.define(
  'c4.Relationship',
  {
    attrs: {
      line: {
        stroke: '#707070',
        strokeWidth: 2,
        strokeDasharray: '5,5',
        targetMarker: {
          type: 'path',
          d: 'M 10 -5 0 0 10 5 z',
          fill: '#707070',
        },
      },
      wrapper: {
        strokeWidth: 20,
      },
    },
    labels: [
      {
        position: 0.5,
        attrs: {
          text: {
            text: '',
            fill: '#707070',
            fontSize: 11,
            fontFamily: 'Open Sans, sans-serif',
            textAnchor: 'middle',
            textVerticalAnchor: 'middle',
          },
          rect: {
            fill: '#ffffff',
            rx: 3,
            ry: 3,
            ref: 'text',
            refX: -5,
            refY: -3,
            refWidth: '100%',
            refHeight: '100%',
            refWidth2: 10,
            refHeight2: 6,
          },
        },
        markup: [
          {
            tagName: 'rect',
            selector: 'rect',
          },
          {
            tagName: 'text',
            selector: 'text',
          },
        ],
      },
    ],
  }
)

export interface RelationshipStyle {
  color?: string
  dashed?: boolean
  thickness?: number
  sequenceNumber?: string
}

export function createRelationship(
  id: string,
  sourceId: string,
  targetId: string,
  description: string = '',
  technology: string = '',
  style?: RelationshipStyle,
  vertices?: Array<{ x: number; y: number }>
): joint.shapes.standard.Link {
  // Build label text - include sequence number if present
  let labelText = technology
    ? `${description}\n[${technology}]`
    : description

  // Add sequence number prefix if provided
  const sequencePrefix = style?.sequenceNumber ? `${style.sequenceNumber}. ` : ''
  if (sequencePrefix && labelText) {
    labelText = sequencePrefix + labelText
  } else if (sequencePrefix) {
    labelText = style?.sequenceNumber || ''
  }

  const labels: any[] = []

  // Add main description label
  if (labelText) {
    labels.push({
      position: 0.5,
      attrs: {
        text: {
          text: labelText,
          ...(style?.color && { fill: style.color }),
        },
      },
    })
  }

  // Add sequence number badge at the start of the link
  if (style?.sequenceNumber) {
    labels.push({
      position: 0.1,
      attrs: {
        text: {
          text: style.sequenceNumber,
          fill: '#ffffff',
          fontSize: 11,
          fontWeight: 'bold',
          fontFamily: 'Open Sans, sans-serif',
        },
        rect: {
          fill: style?.color || '#707070',
          rx: 10,
          ry: 10,
          ref: 'text',
          refX: -8,
          refY: -5,
          refWidth: '100%',
          refHeight: '100%',
          refWidth2: 16,
          refHeight2: 10,
        },
      },
      markup: [
        { tagName: 'rect', selector: 'rect' },
        { tagName: 'text', selector: 'text' },
      ],
    })
  }

  const relationship = new C4Relationship({
    id,
    source: { id: sourceId },
    target: { id: targetId },
    labels,
    attrs: {
      line: {
        ...(style?.color && {
          stroke: style.color,
          targetMarker: {
            type: 'path',
            d: 'M 10 -5 0 0 10 5 z',
            fill: style.color,
          },
        }),
        ...(style?.dashed !== undefined && {
          strokeDasharray: style.dashed ? '5,5' : '0',
        }),
        ...(style?.thickness && { strokeWidth: style.thickness }),
      },
    },
    ...(vertices && { vertices }),
  })

  return relationship
}

/**
 * Create a synchronous relationship (solid line)
 */
export function createSyncRelationship(
  id: string,
  sourceId: string,
  targetId: string,
  description: string = '',
  technology: string = '',
  style?: RelationshipStyle,
  vertices?: Array<{ x: number; y: number }>
): joint.shapes.standard.Link {
  return createRelationship(id, sourceId, targetId, description, technology, {
    ...style,
    dashed: false,
  }, vertices)
}

/**
 * Create an asynchronous relationship (dashed line)
 */
export function createAsyncRelationship(
  id: string,
  sourceId: string,
  targetId: string,
  description: string = '',
  technology: string = '',
  style?: RelationshipStyle,
  vertices?: Array<{ x: number; y: number }>
): joint.shapes.standard.Link {
  return createRelationship(id, sourceId, targetId, description, technology, {
    ...style,
    dashed: true,
  }, vertices)
}
